import React from "react";
import moment from "moment";
import i18next from "i18next";
import { StatusBadge, BadgeTone } from "../../../atoms/StatusBadge";

export type OwnerEmailTarget =
  | { mode: "single"; entityIdentification: string }
  | { mode: "bulk"; entityIdentifications: string[] };

export interface Entity {
  entity_identification: string;
  entity_name: string;
  entity_status: string;
  creation_date: string;
  active_users: number;
  business_card_enabled: boolean;
  loyalty_program_enabled: boolean;
  features?: string[];
  owner_email?: string | null;
  owner_country_code?: string | null;
  owner_user_is_active?: boolean | null;
}

export interface RawFeatureLink {
  feature: string;
  route: string;
  process_identification: string;
}

export interface EntityDetails {
  entity_identification: string;
  entity_name: string;
  entity_description: string;
  entity_status: string;
  creation_date: string;
  owner_email: string;
  owner_country_code: string;
  owner_user_is_active?: boolean | null;
  total_users: number;
  last_connection_datetime: string;
  business_card_enabled: boolean;
  loyalty_program_enabled: boolean;
  features?: string[];
  feature_links?: RawFeatureLink[];
  acquisition_tracking?: Record<string, unknown> | null;
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_entities: number;
  entities_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface EntitiesData {
  entities: Entity[];
  pagination: Pagination;
}

export interface ConnectionByDate {
  date: string;
  connections: number;
}

export interface ConnectionsStats {
  filter: {
    start_date: string;
    end_date: string;
  };
  total_connections: number;
  connections_by_date: ConnectionByDate[];
}

/**
 * A configured feature and its optional shareable public link. The drawer consumes an optional
 * `featureLinks?: FeatureLink[]` prop; when it is undefined it gracefully falls back to the plain
 * `features?: string[]` badge cloud.
 */
export interface FeatureLink {
  feature: string;
  label: string;
  publicUrl: string | null;
}

export const buildFeatureLinks = (
  featureLinks: RawFeatureLink[] | undefined
): FeatureLink[] | undefined => {
  if (!featureLinks || featureLinks.length === 0) return undefined;
  const host = process.env.REACT_APP_HOST_URL || "";
  return featureLinks.map((link) => ({
    feature: link.feature,
    label: getFeatureLabel(link.feature),
    publicUrl:
      host && link.process_identification
        ? `${host}/${link.route}/${link.process_identification}`
        : null,
  }));
};

export const FEATURE_LABELS: Record<string, string> = {
  ai_engine_settings: "AI engine",
  business_card_form: "Business cards",
  custom_registration: "Custom registration",
  esign: "E-signature",
  events_config: "Events",
  gaming: "Gaming",
  leads_config: "Leads",
  lmc_form: "Loyalty",
  login_form: "Login form",
  todos_config: "Tasks",
  webhook_url: "Webhooks",
};

export const getFeatureLabel = (feature: string): string =>
  FEATURE_LABELS[feature] ?? feature;

export const FeatureBadges: React.FC<{ features?: string[] }> = ({
  features,
}) => {
  if (!features || features.length === 0) {
    return <span className="text-xs text-gray-400">None</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {features.map((f) => (
        <StatusBadge key={f} color="gray">
          {getFeatureLabel(f)}
        </StatusBadge>
      ))}
    </div>
  );
};

const ENTITY_STATUS_LABELS: Record<string, string> = {
  NEW: "New",
  SERVICE_ON: "Service active",
  SERVICE_OFF: "Service suspended",
  CANCELED: "Cancelled",
};

export const getEntityStatusLabel = (status: string): string =>
  ENTITY_STATUS_LABELS[status] ?? status;

const ENTITY_STATUS_TONE: Record<string, BadgeTone> = {
  NEW: "gray",
  SERVICE_ON: "success",
  SERVICE_OFF: "warning",
  CANCELED: "failure",
};

export const getStatusBadge = (status: string) => (
  <StatusBadge color={ENTITY_STATUS_TONE[status] || "gray"}>
    {getEntityStatusLabel(status)}
  </StatusBadge>
);

export const getStatusDot = (status: string) => {
  const dotColors: Record<string, string> = {
    NEW: "bg-gray-400",
    SERVICE_ON: "bg-green-500",
    SERVICE_OFF: "bg-amber-400",
    CANCELED: "bg-red-500",
  };
  const label = getEntityStatusLabel(status);
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${
        dotColors[status] || "bg-gray-400"
      }`}
      title={label}
      aria-label={label}
    />
  );
};

export const getBooleanBadge = (enabled: boolean) => (
  <StatusBadge color={enabled ? "success" : "gray"}>
    {enabled ? "Enabled" : "Disabled"}
  </StatusBadge>
);

export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "N/A";
  return moment(dateString).format("lll");
};

export const formatAmount = (amount: number, currency: string) => {
  return new Intl.NumberFormat(i18next.language || "en", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const RECURRENCY_LABELS: Record<string, string> = {
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
  ANNUALLY: "Annually",
  YEARLY: "Annually",
  UNIQUE: "One-time",
};

export const getRecurrencyLabel = (recurrency: string): string =>
  (RECURRENCY_LABELS[recurrency] || recurrency).toUpperCase();
